import React, { FunctionComponent, useState } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Box, Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useGetCompanyOrderStatusList } from '../../hooks/api/oms'
import moment from 'moment'
import { useQueryClient } from '@tanstack/react-query'


const CheckOrderStatusMultipleResults: FunctionComponent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orderNumber } = useParams();
  const location = useLocation();
  
  // Determine search type from the current path
  const isPoSearch = location.pathname.includes('/po/');
  const isSoSearch = location.pathname.includes('/so/');
  
  const { data: orderStatusList, isFetched: hasFetched, isLoading, error } = useGetCompanyOrderStatusList(
    isSoSearch ? orderNumber : undefined,
    isPoSearch ? orderNumber : undefined
  );

  // Deduplicate orders based on SO number and customer
  const uniqueOrders = React.useMemo(() => {
    if (!orderStatusList || orderStatusList.length === 0) return [];
    
    const seen = new Set();
    return orderStatusList.filter((order: any) => {
      const key = `${order.orderHeader?.orderNumber}-${order.orderHeader?.customer}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [orderStatusList]);

  // If only 1 unique order, redirect to single order details page
  React.useEffect(() => {
    if (hasFetched && uniqueOrders && uniqueOrders.length === 1) {
      const order = uniqueOrders[0];
      const soNumber = order.orderHeader?.orderNumber || order.orderNumber;
      // Pass navigation state to preserve original search context
      navigate(`/order-management-system/check-order-status/order/${soNumber}`, {
        state: { 
          fromMultipleResults: false, // This is auto-redirect, not user click
          searchType: isPoSearch ? 'po' : 'so', 
          searchNumber: orderNumber, // Original search term
          originalSearchType: isPoSearch ? 'po' : 'so',
          originalSearchNumber: orderNumber
        }
      });
    }
  }, [hasFetched, uniqueOrders, navigate, isPoSearch, orderNumber]);

  const handleBack = () => {
    queryClient.clear();
    // Always go back to the search form from multiple results page
    navigate('/order-management-system/check-order-status');
  };

  const handleViewOrder = (orderData: any) => {
    // Use the actual SO number from orderHeader, not the search parameter
    const soNumber = orderData.orderHeader?.orderNumber || orderData.orderNumber;
    // Pass navigation context to indicate we came from multiple results
    navigate(`/order-management-system/check-order-status/order/${soNumber}`, {
      state: { fromMultipleResults: true, searchType: isPoSearch ? 'po' : 'so', searchNumber: orderNumber }
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
        <Alert severity="info" sx={{ mt: 3 }}>
          Loading order status details for {isPoSearch ? 'PO' : 'SO'}: <strong>{orderNumber}</strong>...
        </Alert>
      </DashboardLayout>
    );
  }

  // Show loading message when redirecting to single order
  if (hasFetched && uniqueOrders && uniqueOrders.length === 1) {
    return (
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
        <Alert severity="info" sx={{ mt: 3 }}>
          Found single order, redirecting to order details...
        </Alert>
      </DashboardLayout>
    );
  }

  if (error) {
    const axiosError = error as any;
    const errorMessage = axiosError?.response?.data?.error || axiosError?.response?.data?.message || 'Error retrieving order status';
    
    return (
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
        <Alert severity="error" sx={{ mt: 3 }}>
          {errorMessage}
        </Alert>
        <Box sx={{ mt: 3 }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={handleBack}>
            BACK
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  if (!hasFetched || !uniqueOrders || uniqueOrders.length === 0) {
    return (
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
        <Alert severity="warning" sx={{ mt: 3 }}>
          No orders found for {isPoSearch ? 'PO' : 'SO'}: <strong>{orderNumber}</strong>
        </Alert>
        <Box sx={{ mt: 3 }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={handleBack}>
            BACK
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  // Only show multiple results page if there are 2 or more unique orders
  if (uniqueOrders.length < 2) {
    return (
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
        <Alert severity="info" sx={{ mt: 3 }}>
          Found single order, redirecting to order details...
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
      <Box sx={{ mt: 2 }}>
        <Typography variant="h5" gutterBottom>
          {uniqueOrders.length > 1 ? 'Multiple Orders Found' : 'Order Found'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Found {uniqueOrders.length} unique order{uniqueOrders.length > 1 ? 's' : ''} for {isPoSearch ? 'PO' : 'SO'}: <strong>{orderNumber}</strong>
        </Typography>

        {uniqueOrders.length > 1 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Multiple orders found with the same {isPoSearch ? 'Purchase Order' : 'Sales Order'} number. 
            Please select the order you want to view details for.
          </Alert>
        )}

        <Grid container spacing={3}>
          {uniqueOrders.map((order: any, index: number) => (
            <Grid size={12} key={order.orderNumber || index}>
              <Card sx={{ border: '1px solid #e0e0e0', '&:hover': { boxShadow: 3 } }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={8}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Sales Order: {order.orderHeader?.orderNumber || order.orderNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Customer: {order.orderHeader?.customer || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Order Date: {order.orderHeader?.orderDate ? moment(order.orderHeader.orderDate).format('MM/DD/YYYY') : 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Purchase Order: {order.orderHeader?.customerPo || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {order.orderType || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={4} sx={{ textAlign: 'right' }}>
                      <Button
                        variant="contained"
                        onClick={() => handleViewOrder(order)}
                        sx={{ 
                          backgroundColor: '#c3183c', 
                          padding: '8px 20px',
                          '&:hover': {
                            backgroundColor: '#a0152a'
                          }
                        }}
                      >
                        View Details
                      </Button>
                    </Grid>
                  </Grid>

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={handleBack}>
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default CheckOrderStatusMultipleResults;
